import {AnyAction, ThunkDispatch} from '@reduxjs/toolkit'
import bookApi from 'middleware/bookApi'
import {FatalErrorAction} from 'modules/common/actions/errors'
import offersV3MappingMiddleware from 'modules/sapiSearch/middleware/offersV3MappingMiddleware'
import {searchProgressMiddleware} from 'modules/sapiSearch/middleware/searchProgress'
import {Middleware} from 'redux'
import createSentryMiddleware from 'redux-sentry-middleware'
import {RootState, ThunkExtra} from 'store'

import coreAnalytics from '@vio/core/src/analytics/modules/middleware'
import {
  afterAuthRedirection,
  removeUserData,
  removeUserProfile
} from '@vio/core/src/auth/modules/middleware'
import {State} from '@vio/core/src/auth/modules/selectors'
import {SearchApiActionsType} from '@vio/core/src/sapi/services/searchApi/action'
import {dealFreezeMiddleware} from '@vio/shared/src/dealFreeze/modules/middleware'

import {ModuleActionType as SearchActionType} from '../modules/search/actions'
import accommodationMiddleware from './accommodation'
import analytics from './analytics'
import {ModuleActionType as AnalyticsActionType} from './analytics/actions'
import searchApiAnalytics from './analytics/searchApiAnalytics'
import bookingManagementMiddleware from './bookingManagement'
import errorHandling from './errorHandling'
import marketingNotificationsMiddleware from './marketingNotifications'
import navigation from './navigation'
import {ModuleActionType as NavigationActionType} from './navigation/actions'
import paymentSettingsApiMiddleware from './paymentSettingsApi'
import persistence from './persistence'
import proactiveOfferCheck from './proactiveOfferCheck'
import searchApi from './searchApi'
import userSessionMonitoring from './userSessionMonitoring'

export type StoreActionsType =
  | SearchActionType
  | AnalyticsActionType
  | NavigationActionType
  | FatalErrorAction
  | SearchApiActionsType

export type MiddlewareType = Middleware<
  StoreActionsType,
  RootState,
  ThunkDispatch<RootState, ThunkExtra, AnyAction>
>

const middlewareList = [
  analytics,
  errorHandling,
  userSessionMonitoring,
  navigation,
  persistence,
  searchApi,
  coreAnalytics,
  searchApiAnalytics,
  offersV3MappingMiddleware,
  searchProgressMiddleware,
  dealFreezeMiddleware,
  accommodationMiddleware,
  bookingManagementMiddleware,
  paymentSettingsApiMiddleware,
  afterAuthRedirection,
  marketingNotificationsMiddleware,
  proactiveOfferCheck,
  bookApi,
  ...(typeof Sentry === 'undefined'
    ? []
    : [
        // @ts-expect-error - Sentry and middleware versions don't match
        createSentryMiddleware(Sentry, {
          stateTransformer: (state: State) => removeUserProfile(state),
          actionTransformer: action => removeUserData(action)
        })
      ])
]

const jsDomSafeMiddlewareList = [
  // analytics, // Writing script tags fails
  searchApiAnalytics,
  errorHandling,
  // userSessionMonitoring, // No need to track sessions in tests
  navigation,
  persistence,
  bookingManagementMiddleware
]

// Some redux middleware has side-effects like writing script tags that can fail
// Replace middleware list for test and storybook to only include middleware that works in those environments
const isTestEnvironment =
  process.env.STORYBOOK || process.env.NODE_ENV === 'test'
const safeMiddlewareList = isTestEnvironment
  ? jsDomSafeMiddlewareList
  : middlewareList

export default safeMiddlewareList
